import React, { useEffect, useRef, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { BeatLoader } from "react-spinners";
import { toast } from "react-toastify";
import { useTranslation } from "react-i18next";
import { localizedStoreProductName } from "../../../utils/storeUtils";
import "../lastProjects/LastProjects.css";
import "./Orders.css";
import "./DashboardOrderDetail.css";

const STATUS_OPTIONS = [
  "pending",
  "confirmed",
  "shipped",
  "delivered",
  "cancelled",
];

function formatDate(dateStr) {
  if (!dateStr) return "—";
  return new Date(dateStr).toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
}

function formatPayment(method) {
  return method === "cod" ? "Cash on delivery" : "Online";
}

/** Structured coords from API, or parse legacy line in address: "📍 Map: lat, lng" */
function getDeliveryMapCoords(customer) {
  if (!customer) return null;
  const lat = Number(customer.latitude);
  const lng = Number(customer.longitude);
  if (
    Number.isFinite(lat) &&
    Number.isFinite(lng) &&
    lat >= -90 &&
    lat <= 90 &&
    lng >= -180 &&
    lng <= 180
  ) {
    return { lat, lng };
  }
  const addr = String(customer.address || "");
  const legacy = addr.match(/📍\s*Map:\s*([\d.+-]+)\s*,\s*([\d.+-]+)/);
  if (legacy) {
    const la = Number(legacy[1]);
    const ln = Number(legacy[2]);
    if (
      Number.isFinite(la) &&
      Number.isFinite(ln) &&
      la >= -90 &&
      la <= 90 &&
      ln >= -180 &&
      ln <= 180
    ) {
      return { lat: la, lng: ln };
    }
  }
  return null;
}

export default function DashboardOrderDetail() {
  const { i18n } = useTranslation("global");
  const shareLinkInputRef = useRef(null);
  const { orderId } = useParams();
  const navigate = useNavigate();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [trackingForm, setTrackingForm] = useState({
    shippingCompany: "",
    trackingNumber: "",
    estimatedDeliveryDate: "",
  });

  const token =
    localStorage.getItem("authToken") || sessionStorage.getItem("authToken");

  useEffect(() => {
    if (!token) {
      navigate("/dashboard/login");
      return;
    }
    let cancelled = false;
    axios
      .get(`/orders/${orderId}`, {
        headers: { Authorization: `Bearer ${token}` },
      })
      .then(({ data }) => {
        if (!cancelled) {
          setOrder(data);
          setTrackingForm({
            shippingCompany: data?.tracking?.shippingCompany || "",
            trackingNumber: data?.tracking?.trackingNumber || "",
            estimatedDeliveryDate: data?.tracking?.estimatedDeliveryDate
              ? data.tracking.estimatedDeliveryDate.slice(0, 10)
              : "",
          });
        }
      })
      .catch((err) => {
        if (!cancelled) {
          if (err.response?.status === 404 || err.response?.status === 403) {
            toast.error("Order not found.");
            navigate("/dashboard/orders");
          } else {
            toast.error("Failed to load order.");
          }
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => { cancelled = true; };
  }, [orderId, token, navigate]);

  const handleStatusChange = (newStatus) => {
    if (!order?._id || saving) return;
    setSaving(true);
    axios
      .put(
        `/orders/${order._id}/status`,
        { status: newStatus },
        { headers: { Authorization: `Bearer ${token}` } }
      )
      .then(({ data }) => {
        setOrder(data);
        toast.success("Order status updated.");
      })
      .catch(() => toast.error("Failed to update status."))
      .finally(() => setSaving(false));
  };

  const handleTrackingSave = (e) => {
    e.preventDefault();
    if (!order?._id || saving) return;
    setSaving(true);
    const payload = {
      tracking: {
        shippingCompany: trackingForm.shippingCompany || undefined,
        trackingNumber: trackingForm.trackingNumber || undefined,
        estimatedDeliveryDate: trackingForm.estimatedDeliveryDate
          ? new Date(trackingForm.estimatedDeliveryDate).toISOString()
          : undefined,
      },
    };
    axios
      .put(`/orders/${order._id}/status`, payload, {
        headers: { Authorization: `Bearer ${token}` },
      })
      .then(({ data }) => {
        setOrder(data);
        setTrackingForm({
          shippingCompany: data?.tracking?.shippingCompany || "",
          trackingNumber: data?.tracking?.trackingNumber || "",
          estimatedDeliveryDate: data?.tracking?.estimatedDeliveryDate
            ? data.tracking.estimatedDeliveryDate.slice(0, 10)
            : "",
        });
        toast.success("Shipping info updated.");
      })
      .catch(() => toast.error("Failed to update shipping info."))
      .finally(() => setSaving(false));
  };

  if (loading || !order) {
    return (
      <main className="lastProjects dashboard-order-detail">
        <div className="container">
          <div className="loader-container">
            <BeatLoader />
          </div>
        </div>
      </main>
    );
  }

  const tracking = order.tracking || {};
  const mapCoords = getDeliveryMapCoords(order.customer);
  const osmOpenUrl = mapCoords
    ? `https://www.openstreetmap.org/?mlat=${mapCoords.lat}&mlon=${mapCoords.lng}#map=16/${mapCoords.lat}/${mapCoords.lng}`
    : null;
  const osmEmbedUrl = mapCoords
    ? `https://www.openstreetmap.org/export/embed.html?bbox=${encodeURIComponent(
        `${mapCoords.lng - 0.01},${mapCoords.lat - 0.008},${mapCoords.lng + 0.01},${mapCoords.lat + 0.008}`
      )}&layer=mapnik&marker=${encodeURIComponent(`${mapCoords.lat},${mapCoords.lng}`)}`
    : null;
  /** Opens in Google Maps (app or web) — best link to copy / send */
  const shareMapsUrl = mapCoords
    ? `https://www.google.com/maps?q=${mapCoords.lat},${mapCoords.lng}`
    : null;
  const whatsAppShareUrl = shareMapsUrl
    ? `https://wa.me/?text=${encodeURIComponent(shareMapsUrl)}`
    : null;

  async function copyShareLink() {
    if (!shareMapsUrl) return;
    try {
      await navigator.clipboard.writeText(shareMapsUrl);
      toast.success("تم نسخ الرابط — Link copied");
    } catch {
      try {
        const el = shareLinkInputRef.current;
        if (el) {
          el.focus();
          el.select();
          document.execCommand("copy");
          toast.success("تم نسخ الرابط — Link copied");
        }
      } catch {
        toast.error("لم نتمكن من النسخ — Copy failed");
      }
    }
  }

  return (
    <main className="lastProjects dashboard-order-detail">
      <div className="container">
        <Link to="/dashboard/orders" className="dashboard-order-detail-back">
          <i className="fa-solid fa-arrow-left" aria-hidden />
          Back to Orders
        </Link>

        <div className="title dashboard-order-detail-title">
          <h1>Order #{String(order._id).slice(-8).toUpperCase()}</h1>
          <span
            className="dashboard-orders-status-badge dashboard-order-detail-status"
            style={{
              backgroundColor:
                {
                  delivered: "#059669",
                  shipped: "#2563eb",
                  processing: "#ea580c",
                  confirmed: "#ea580c",
                  pending: "#d97706",
                  cancelled: "#6b7280",
                }[order.status?.toLowerCase()] || "#6b7280",
              color: "#fff",
            }}
          >
            {order.status || "pending"}
          </span>
        </div>

        <div className="dashboard-order-detail-grid">
          <section className="dashboard-order-detail-card">
            <h2 className="dashboard-order-detail-card-title">Items</h2>
            <ul className="dashboard-order-detail-items">
              {(order.items || []).map((item, i) => (
                <li key={i} className="dashboard-order-detail-item">
                  {item.imageUrl ? (
                    <img
                      src={item.imageUrl}
                      alt=""
                      className="dashboard-order-detail-item-img"
                    />
                  ) : (
                    <div className="dashboard-order-detail-item-placeholder" />
                  )}
                  <div className="dashboard-order-detail-item-info">
                    <span className="dashboard-order-detail-item-name">
                      {localizedStoreProductName(item, i18n.language)}
                    </span>
                    {item.sku ? (
                      <span
                        className="dashboard-order-detail-item-meta dashboard-order-detail-item-sku"
                        dir="ltr"
                      >
                        Code: {item.sku}
                      </span>
                    ) : null}
                    <span className="dashboard-order-detail-item-meta dashboard-order-detail-item-color">
                      Color: {item.selectedColor || "Not selected"}
                      {item.selectedColorHex && (
                        <span
                          className="dashboard-order-detail-item-color-dot"
                          style={{ backgroundColor: item.selectedColorHex }}
                          aria-label={`Selected color ${item.selectedColorHex}`}
                          title={item.selectedColorHex}
                        />
                      )}
                    </span>
                    <span className="dashboard-order-detail-item-meta">
                      {item.quantity} × {Number(item.price || 0).toFixed(2)} SAR
                    </span>
                  </div>
                  <span className="dashboard-order-detail-item-total">
                    {(item.quantity * (item.price || 0)).toFixed(2)} SAR
                  </span>
                </li>
              ))}
            </ul>
            <div className="dashboard-order-detail-totals">
              <div className="dashboard-order-detail-totals-row">
                <span>Subtotal</span>
                <span>
                  {Number(order.totals?.subtotal ?? 0).toFixed(2)}{" "}
                  {order.totals?.currency || "SAR"}
                </span>
              </div>
              {Number(order.totals?.shipping ?? 0) > 0 && (
                <div className="dashboard-order-detail-totals-row">
                  <span>Shipping</span>
                  <span>
                    {Number(order.totals.shipping).toFixed(2)}{" "}
                    {order.totals.currency || "SAR"}
                  </span>
                </div>
              )}
              <div className="dashboard-order-detail-totals-row total">
                <span>Total</span>
                <span>
                  {Number(order.totals?.total ?? 0).toFixed(2)}{" "}
                  {order.totals?.currency || "SAR"}
                </span>
              </div>
            </div>
          </section>

          <section className="dashboard-order-detail-card">
            <h2 className="dashboard-order-detail-card-title">
              Customer & Delivery
            </h2>
            <p className="dashboard-order-detail-address">
              <strong>{order.customer?.fullName}</strong>
              <br />
              {order.customer?.address}
              <br />
              {order.customer?.phone}
              {order.customer?.email ? ` • ${order.customer.email}` : ""}
              {order.customer?.note ? (
                <>
                  <br />
                  <em>Note: {order.customer.note}</em>
                </>
              ) : null}
            </p>
            {mapCoords && osmOpenUrl && osmEmbedUrl && shareMapsUrl ? (
              <div className="dashboard-order-detail-map">
                <h3 className="dashboard-order-detail-map-title">
                  موقع التسليم على الخريطة / Delivery on map
                </h3>
                <p className="dashboard-order-detail-coords">
                  {mapCoords.lat.toFixed(6)}, {mapCoords.lng.toFixed(6)}
                </p>
                <div className="dashboard-order-detail-map-links-row">
                  <a
                    href={osmOpenUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="dashboard-order-detail-map-link"
                  >
                    OpenStreetMap
                  </a>
                  <a
                    href={shareMapsUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="dashboard-order-detail-map-link"
                  >
                    Google Maps
                  </a>
                </div>
                <iframe
                  title="Customer delivery location"
                  className="dashboard-order-detail-map-frame"
                  src={osmEmbedUrl}
                  loading="lazy"
                />
                <div className="dashboard-order-detail-share">
                  <span className="dashboard-order-detail-share-label">
                    رابط للنسخ أو الإرسال / Link to copy or send
                  </span>
                  <div className="dashboard-order-detail-share-row">
                    <input
                      ref={shareLinkInputRef}
                      type="text"
                      readOnly
                      className="dashboard-order-detail-share-input"
                      value={shareMapsUrl}
                      onFocus={(e) => e.target.select()}
                      aria-label="Shareable maps link"
                    />
                    <button
                      type="button"
                      className="dashboard-order-detail-share-copy"
                      onClick={copyShareLink}
                    >
                      Copy
                    </button>
                    {whatsAppShareUrl ? (
                      <a
                        href={whatsAppShareUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="dashboard-order-detail-share-wa"
                        aria-label="Send map link via WhatsApp"
                      >
                        <i className="fa-brands fa-whatsapp" aria-hidden />
                        <span className="dashboard-order-detail-share-wa-text">WhatsApp</span>
                      </a>
                    ) : null}
                  </div>
                </div>
              </div>
            ) : null}
          </section>

          <section className="dashboard-order-detail-card">
            <h2 className="dashboard-order-detail-card-title">Payment</h2>
            <p>
              {formatPayment(order.payment?.method)} /{" "}
              {order.payment?.status || "—"}
            </p>
          </section>

          <section className="dashboard-order-detail-card">
            <h2 className="dashboard-order-detail-card-title">
              Update order status
            </h2>
            <select
              value={order.status || "pending"}
              onChange={(e) => handleStatusChange(e.target.value)}
              disabled={saving}
              className="orders-status-filter dashboard-order-detail-status-select"
            >
              {STATUS_OPTIONS.map((s) => (
                <option key={s} value={s}>
                  {s}
                </option>
              ))}
            </select>
          </section>

          <section className="dashboard-order-detail-card">
            <h2 className="dashboard-order-detail-card-title">
              Shipping / Tracking
            </h2>
            <form onSubmit={handleTrackingSave} className="dashboard-order-detail-tracking-form">
              <label>
                Shipping company
                <input
                  type="text"
                  value={trackingForm.shippingCompany}
                  onChange={(e) =>
                    setTrackingForm((f) => ({
                      ...f,
                      shippingCompany: e.target.value,
                    }))
                  }
                  placeholder="e.g. Aramex"
                  className="dashboard-order-detail-input"
                />
              </label>
              <label>
                Tracking number
                <input
                  type="text"
                  value={trackingForm.trackingNumber}
                  onChange={(e) =>
                    setTrackingForm((f) => ({
                      ...f,
                      trackingNumber: e.target.value,
                    }))
                  }
                  placeholder="Tracking number"
                  className="dashboard-order-detail-input"
                />
              </label>
              <label>
                Estimated delivery date
                <input
                  type="date"
                  value={trackingForm.estimatedDeliveryDate}
                  onChange={(e) =>
                    setTrackingForm((f) => ({
                      ...f,
                      estimatedDeliveryDate: e.target.value,
                    }))
                  }
                  className="dashboard-order-detail-input"
                />
              </label>
              {tracking.shippedAt && (
                <p className="dashboard-order-detail-muted">
                  Shipped at: {formatDate(tracking.shippedAt)}
                </p>
              )}
              {tracking.deliveredAt && (
                <p className="dashboard-order-detail-muted">
                  Delivered at: {formatDate(tracking.deliveredAt)}
                </p>
              )}
              <button
                type="submit"
                disabled={saving}
                className="dashboard-order-detail-btn"
              >
                {saving ? "Saving…" : "Save shipping info"}
              </button>
            </form>
          </section>

          {(order.returnRequests || []).length > 0 && (
            <section className="dashboard-order-detail-card dashboard-order-detail-returns">
              <h2 className="dashboard-order-detail-card-title">
                Return / Exchange requests
              </h2>
              <ul className="dashboard-order-detail-return-list">
                {order.returnRequests.map((req, i) => (
                  <li key={i} className="dashboard-order-detail-return-item">
                    <span className="dashboard-order-detail-return-type">
                      {req.type === "return" ? "Return" : "Exchange"}
                    </span>
                    <span
                      className={`dashboard-order-detail-return-status ${req.status}`}
                    >
                      {req.status}
                    </span>
                    <p className="dashboard-order-detail-return-reason">
                      {req.reason}
                    </p>
                    {req.imageUrl ? (
                      <a
                        href={req.imageUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="dashboard-order-detail-return-link"
                      >
                        View image
                      </a>
                    ) : null}
                  </li>
                ))}
              </ul>
            </section>
          )}
        </div>
      </div>
    </main>
  );
}
